import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useScreenTime } from '../../hooks/useScreenTime';
import { ScreenTimeGate } from '../Parent/ScreenTimeGate';
import { useOnboardingStatus } from '../../hooks/useOnboardingStatus';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Auth gate for every signed-in route, and — since 2026-09-05 — the single
 * enforcement point for parental screen-time limits (audit finding C3).
 *
 * Why here: `ScreenTimeGate` was built months ago and mounted nowhere, so
 * limits a parent set were displayed as saved and then ignored. This component
 * already wraps all 55 authenticated routes, which makes it the one place that
 * covers the whole signed-in surface without touching every call site. Public
 * routes (/login, /register, /consent, /terms, /privacy, /play/:id) sit outside
 * it and stay reachable, which matters: a locked-out child must still be able
 * to reach the legal pages, and a parent must be able to reach the consent flow.
 *
 * Fail-open by design. `checkTimeLimit` returns `allowed: true` when no
 * screen-time settings exist, so accounts with no parental configuration —
 * teachers, admins, adult users — are unaffected, and a Firestore read error
 * leaves the student working rather than locked out. The enforcement that
 * cannot be failed open lives on the server: recorded minutes come from
 * `recordSessionHeartbeat` and the usage document is read-only to clients.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  // Must run before any early return — hooks cannot be called conditionally.
  // Passing undefined while signed out keeps it inert.
  const screenTime = useScreenTime(currentUser?.uid);
  const onboarding = useOnboardingStatus(currentUser?.uid);

  // Show loading state while auth is being determined
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'system-ui'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Preserve the intended destination so Login can redirect back after auth
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Don't flash the lockout before the first status load resolves — but do it
  // WITHOUT changing the shape of the tree.
  //
  // 2026-09-05: this used to `return <>{children}</>` while loading and then
  // switch to `<ScreenTimeGate>{children}</ScreenTimeGate>` once the check
  // resolved. React sees a different element type in that position and
  // unmounts the whole route subtree, so every authenticated route was
  // remounted a few hundred milliseconds after it appeared — discarding any
  // state the child had accumulated. A child who started typing their game
  // name on /project/new immediately after it loaded watched the field empty
  // itself. Found as an intermittent E2E failure, which is what an
  // intermittent user-facing bug looks like from a test.
  //
  // `ScreenTimeGate` already renders children unobstructed for the loading
  // status (`DEFAULT_STATUS` has `isOverLimit: false`), so mounting it
  // unconditionally is both simpler and stable.
  // S54: an account with no age on record cannot be allowed to stay. `AcceptTerms`
  // writes `birthDate` + `requiresParentalConsent`, and `Login` routes a FIRST-TIME
  // federated user there — but nothing enforced completion, nothing read
  // `termsAcceptedAt`, and a returning federated user came straight here. A child who
  // closed the tab on /accept-terms once was in permanently with no age, and
  // `requiresConsent()` in firestore.rules reads a missing `requiresParentalConsent` as
  // false — so that account was never COPPA-covered.
  //
  // Only on 'incomplete', which means the document was read and has no age. 'loading'
  // and 'unknown' render children, so a transient Firestore failure cannot lock a child
  // out — and rendering children in both of those states keeps the element type in this
  // position stable, which is the S25 remount trap.
  if (onboarding === 'incomplete') {
    return <Navigate to="/accept-terms" state={{ from: location }} replace />;
  }

  return <ScreenTimeGate status={screenTime}>{children}</ScreenTimeGate>;
};
